import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Table,
  Tag,
  Space,
  Button,
  Progress,
  Tooltip,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Tabs,
  Empty,
  Spin,
  Badge,
  Rate,
} from 'antd';
import {
  SafetyCertificateOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SettingOutlined,
  GlobalOutlined,
  SecurityScanOutlined,
  TrophyOutlined,
  AlertOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { marketIntelligenceService } from '../services/api';
import '../styles/Phase5.css';

const MarketIntelligence = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailedRisk, setDetailedRisk] = useState(null);
  const [refreshing, setRefreshing] = useState({});
  const [sourceModalVisible, setSourceModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, profilesData, sourcesData] = await Promise.all([
        marketIntelligenceService.getStats(),
        marketIntelligenceService.getRiskProfiles(),
        marketIntelligenceService.getDataSources(),
      ]);
      setStats(statsData);
      setRiskProfiles(profilesData);
      setDataSources(sourcesData);
    } catch (error) {
      message.error('Failed to load market intelligence data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (vendorId) => {
    setSelectedVendor(vendorId);
    try {
      const details = await marketIntelligenceService.getVendorRisk(vendorId);
      setDetailedRisk(details);
    } catch (error) {
      message.error('Failed to load vendor details');
    }
  };

  const handleRefreshVendor = async (vendorId) => {
    setRefreshing(prev => ({ ...prev, [vendorId]: true }));
    try {
      await marketIntelligenceService.refreshVendorData(vendorId, {
        refresh_pricing: true,
        refresh_risk: true,
        refresh_ratings: true,
        force: true,
      });
      message.success('Vendor data refreshed');
      fetchData();
    } catch (error) {
      message.error('Failed to refresh vendor data');
    } finally {
      setRefreshing(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  const handleAddSource = async (values) => {
    try {
      await marketIntelligenceService.createDataSource(values);
      message.success('Data source added');
      setSourceModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error('Failed to add data source');
    }
  };

  const handleToggleSource = async (sourceId, enabled) => {
    try {
      await marketIntelligenceService.updateDataSource(sourceId, { is_enabled: enabled });
      message.success(enabled ? 'Source enabled' : 'Source disabled');
      fetchData();
    } catch (error) {
      message.error('Failed to update source');
    }
  };

  const getRiskTagClass = (level) => {
    const classes = {
      low: 'p5-tag--success',
      medium: 'p5-tag--warning',
      high: 'p5-tag--danger',
      critical: 'p5-tag--danger',
      unknown: 'p5-tag--neutral',
    };
    return classes[level] || 'p5-tag--neutral';
  };

  const getSecurityStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircleOutlined style={{ color: '#10b981' }} />;
      case 'partial':
        return <ExclamationCircleOutlined style={{ color: '#f59e0b' }} />;
      case 'non_compliant':
        return <CloseCircleOutlined style={{ color: '#ef4444' }} />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const riskColumns = [
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (text, record) => (
        <button
          className="p5-table-primary"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--p5-font-body)',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: 'var(--p5-navy-800)',
            padding: 0,
          }}
          onClick={() => handleViewDetails(record.vendor_id)}
        >
          {text}
        </button>
      ),
    },
    {
      title: 'Risk Level',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (level) => (
        <span className={`p5-tag ${getRiskTagClass(level)}`}>
          <span className="p5-tag-dot" />
          {level?.toUpperCase() || 'UNKNOWN'}
        </span>
      ),
      filters: [
        { text: 'Low', value: 'low' },
        { text: 'Medium', value: 'medium' },
        { text: 'High', value: 'high' },
        { text: 'Critical', value: 'critical' },
      ],
      onFilter: (value, record) => record.risk_level === value,
    },
    {
      title: 'Risk Score',
      dataIndex: 'overall_risk_score',
      key: 'overall_risk_score',
      render: (score) => (
        score !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="p5-progress-bar" style={{ width: '100px' }}>
              <div
                className="p5-progress-fill"
                style={{
                  width: `${score}%`,
                  background: score >= 75 ? 'var(--p5-rose-500)' : score >= 50 ? 'var(--p5-amber-500)' : 'var(--p5-emerald-500)'
                }}
              />
            </div>
            <span className="p5-font-mono" style={{ fontSize: '0.875rem', color: 'var(--p5-navy-700)' }}>{score}</span>
          </div>
        ) : <span className="p5-text-slate">-</span>
      ),
      sorter: (a, b) => (a.overall_risk_score || 0) - (b.overall_risk_score || 0),
    },
    {
      title: 'Financial Health',
      dataIndex: 'financial_health',
      key: 'financial_health',
      render: (health) => {
        const tagClass = { healthy: 'p5-tag--success', stable: 'p5-tag--info', at_risk: 'p5-tag--danger' };
        return health ? (
          <span className={`p5-tag ${tagClass[health] || 'p5-tag--neutral'}`}>
            {health.replace('_', ' ').toUpperCase()}
          </span>
        ) : <span className="p5-text-slate">-</span>;
      },
    },
    {
      title: 'Security',
      dataIndex: 'security_status',
      key: 'security_status',
      render: (status) => (
        <Space>
          {getSecurityStatusIcon(status)}
          <span style={{ color: 'var(--p5-navy-700)' }}>{status?.replace('_', ' ') || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating_average',
      key: 'rating_average',
      render: (rating) =>
        rating ? (
          <Rate
            disabled
            allowHalf
            defaultValue={rating}
            style={{ fontSize: '14px' }}
          />
        ) : <span className="p5-text-slate">-</span>,
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (date) => (
        <span className="p5-table-secondary">
          {date ? new Date(date).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<InfoCircleOutlined />}
              onClick={() => handleViewDetails(record.vendor_id)}
              style={{ color: 'var(--p5-slate-400)' }}
            />
          </Tooltip>
          <Tooltip title="Refresh Data">
            <Button
              type="text"
              icon={<SyncOutlined spin={refreshing[record.vendor_id]} />}
              onClick={() => handleRefreshVendor(record.vendor_id)}
              loading={refreshing[record.vendor_id]}
              style={{ color: 'var(--p5-amber-500)' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const sourceColumns = [
    {
      title: 'Source',
      dataIndex: 'source_name',
      key: 'source_name',
      render: (name, record) => (
        <Space>
          <GlobalOutlined style={{ color: 'var(--p5-amber-500)' }} />
          <span className="p5-table-primary">{name || record.source_type}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'source_type',
      key: 'source_type',
      render: (type) => (
        <span className="p5-tag p5-tag--neutral">{type?.toUpperCase()}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (enabled) => (
        <Badge
          status={enabled ? 'success' : 'default'}
          text={<span style={{ color: enabled ? 'var(--p5-emerald-500)' : 'var(--p5-slate-400)' }}>{enabled ? 'Active' : 'Disabled'}</span>}
        />
      ),
    },
    {
      title: 'Last Sync',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      render: (date) => (
        <span className="p5-table-secondary">
          {date ? new Date(date).toLocaleString() : 'Never'}
        </span>
      ),
    },
    {
      title: 'Sync Status',
      dataIndex: 'last_sync_status',
      key: 'last_sync_status',
      render: (status) => {
        if (!status) return <span className="p5-text-slate">-</span>;
        const tagClass = status === 'success' ? 'p5-tag--success' : status === 'error' ? 'p5-tag--danger' : 'p5-tag--neutral';
        return <span className={`p5-tag ${tagClass}`}>{status.toUpperCase()}</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Switch
            checked={record.is_enabled}
            onChange={(checked) => handleToggleSource(record.id, checked)}
            size="small"
          />
          <Button type="text" icon={<SettingOutlined />} style={{ color: 'var(--p5-slate-400)' }} />
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'risk',
      label: (
        <span className="p5-flex p5-gap-sm">
          <SafetyCertificateOutlined />
          Vendor Risk
        </span>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          <Table
            columns={riskColumns}
            dataSource={riskProfiles}
            rowKey="vendor_id"
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: (
                <div className="p5-empty">
                  <SecurityScanOutlined className="p5-empty-icon" />
                  <div className="p5-empty-title">No risk profiles yet</div>
                  <div className="p5-empty-description">
                    Risk profiles are created automatically when vendors are added to your organization.
                  </div>
                </div>
              ),
            }}
          />
        </div>
      ),
    },
    {
      key: 'pricing',
      label: (
        <span className="p5-flex p5-gap-sm">
          <DollarOutlined />
          Price Benchmarks
        </span>
      ),
      children: (
        <div className="p5-card p5-animate-fadeUp">
          <div className="p5-empty">
            <LineChartOutlined className="p5-empty-icon" />
            <div className="p5-empty-title">Price benchmarking coming soon</div>
            <div className="p5-empty-description">
              Compare your vendor pricing against market data to identify savings opportunities.
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'sources',
      label: (
        <span className="p5-flex p5-gap-sm">
          <SettingOutlined />
          Data Sources
        </span>
      ),
      children: (
        <div className="p5-card p5-animate-fadeUp" style={{ padding: 0 }}>
          <div className="p5-flex-between" style={{ padding: '20px 24px', borderBottom: '1px solid var(--p5-slate-100)' }}>
            <span className="p5-section-title" style={{ margin: 0 }}>
              <GlobalOutlined /> Configured Sources
            </span>
            <Button
              className="p5-btn p5-btn--accent"
              icon={<PlusOutlined />}
              onClick={() => setSourceModalVisible(true)}
            >
              Add Source
            </Button>
          </div>
          <div className="p5-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
            <Table
              columns={sourceColumns}
              dataSource={dataSources}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: (
                  <div className="p5-empty">
                    <GlobalOutlined className="p5-empty-icon" />
                    <div className="p5-empty-title">No data sources configured</div>
                    <div className="p5-empty-description">
                      Connect external data sources to enrich your vendor intelligence.
                    </div>
                    <Button
                      className="p5-btn p5-btn--primary p5-mt-lg"
                      icon={<PlusOutlined />}
                      onClick={() => setSourceModalVisible(true)}
                    >
                      Configure First Source
                    </Button>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p5-page p5-flex-center" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <p className="p5-text-slate p5-mt-md">Loading market intelligence...</p>
        </div>
      </div>
    );
  }

  const avgRiskScore = stats?.average_risk_score || 0;

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header">
        <h1 className="p5-page-title">Market Intelligence</h1>
        <p className="p5-page-subtitle">
          External market data, vendor risk profiles, and competitive price benchmarking
        </p>
      </div>

      {/* Stats Grid */}
      <div className="p5-stats-grid p5-stagger">
        <div className="p5-stat-card p5-animate-fadeUp">
          <div className="p5-stat-icon p5-stat-icon--amber">
            <SafetyCertificateOutlined />
          </div>
          <div className="p5-stat-label">Vendors with Risk Profiles</div>
          <div className="p5-stat-value">{stats?.total_vendors_with_risk_profiles || 0}</div>
        </div>

        <div className="p5-stat-card p5-animate-fadeUp">
          <div className="p5-stat-icon p5-stat-icon--teal">
            <DollarOutlined />
          </div>
          <div className="p5-stat-label">Pricing Records</div>
          <div className="p5-stat-value">{stats?.total_pricing_records || 0}</div>
        </div>

        <div className="p5-stat-card p5-animate-fadeUp">
          <div className="p5-stat-icon p5-stat-icon--violet">
            <GlobalOutlined />
          </div>
          <div className="p5-stat-label">Active Data Sources</div>
          <div className="p5-stat-value">{stats?.active_data_sources || 0}</div>
        </div>

        <div className="p5-stat-card p5-animate-fadeUp">
          <div className="p5-stat-icon p5-stat-icon--rose">
            <AlertOutlined />
          </div>
          <div className="p5-stat-label">Avg Risk Score</div>
          <div className="p5-stat-value" style={{
            color: avgRiskScore >= 50 ? 'var(--p5-rose-500)' : avgRiskScore >= 25 ? 'var(--p5-amber-500)' : 'var(--p5-emerald-500)'
          }}>
            {avgRiskScore.toFixed(1)}
            <span style={{ fontSize: '1rem', color: 'var(--p5-slate-400)', marginLeft: '4px' }}>/ 100</span>
          </div>
        </div>
      </div>

      {/* Risk Distribution & Data Freshness */}
      <Row gutter={24} style={{ marginBottom: '32px' }}>
        <Col xs={24} lg={16}>
          <div className="p5-card p5-animate-fadeUp" style={{ animationDelay: '200ms' }}>
            <div className="p5-card-body">
              <h3 className="p5-section-title">
                <SecurityScanOutlined /> Risk Distribution
              </h3>
              <Row gutter={16}>
                {[
                  { level: 'critical', color: '#ef4444', icon: <CloseCircleOutlined /> },
                  { level: 'high', color: '#f97316', icon: <ExclamationCircleOutlined /> },
                  { level: 'medium', color: '#f59e0b', icon: <ExclamationCircleOutlined /> },
                  { level: 'low', color: '#10b981', icon: <CheckCircleOutlined /> },
                ].map(({ level, color, icon }) => {
                  const count = stats?.vendors_by_risk_level?.[level] || 0;
                  return (
                    <Col span={6} key={level}>
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        background: 'var(--p5-slate-100)',
                        borderRadius: 'var(--p5-radius-md)',
                        transition: 'all var(--p5-transition-base)',
                      }}>
                        <div style={{
                          fontSize: '1.5rem',
                          color,
                          marginBottom: '8px'
                        }}>
                          {icon}
                        </div>
                        <div className="p5-font-display" style={{
                          fontSize: '2rem',
                          color,
                          lineHeight: 1.2
                        }}>
                          {count}
                        </div>
                        <div className="p5-label" style={{ marginTop: '4px' }}>
                          {level}
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="p5-card p5-card--featured p5-animate-fadeUp" style={{ animationDelay: '300ms', height: '100%' }}>
            <div className="p5-card-body" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <SyncOutlined style={{ fontSize: '1.25rem', color: 'var(--p5-amber-400)' }} />
                <span style={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--p5-slate-200)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Data Freshness
                </span>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>
                  Stale Profiles (30+ days)
                </div>
                <div className="p5-font-display" style={{
                  fontSize: '2.5rem',
                  color: (stats?.stale_profiles_count || 0) > 0 ? 'var(--p5-amber-400)' : 'var(--p5-teal-400)'
                }}>
                  {stats?.stale_profiles_count || 0}
                </div>
              </div>
              <div style={{
                paddingTop: '16px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                color: 'var(--p5-slate-300)',
                fontSize: '0.875rem'
              }}>
                Last refresh: {stats?.last_data_refresh
                  ? new Date(stats.last_data_refresh).toLocaleDateString()
                  : 'Never'}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        defaultActiveKey="risk"
        items={tabItems}
        className="p5-tabs"
      />

      {/* Vendor Details Modal */}
      <Modal
        title={null}
        open={selectedVendor !== null && detailedRisk !== null}
        onCancel={() => {
          setSelectedVendor(null);
          setDetailedRisk(null);
        }}
        width={900}
        className="p5-modal"
        footer={[
          <Button key="close" className="p5-btn p5-btn--ghost" onClick={() => setSelectedVendor(null)}>
            Close
          </Button>,
          <Button
            key="refresh"
            className="p5-btn p5-btn--accent"
            icon={<SyncOutlined />}
            onClick={() => handleRefreshVendor(selectedVendor)}
            loading={refreshing[selectedVendor]}
          >
            Refresh Data
          </Button>,
        ]}
      >
        {detailedRisk && (
          <div>
            {/* Modal Header */}
            <div style={{
              background: 'var(--p5-navy-900)',
              margin: '-24px -24px 24px',
              padding: '32px 24px',
              borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
            }}>
              <h2 className="p5-font-display" style={{
                fontSize: '1.75rem',
                color: '#fff',
                margin: 0
              }}>
                {detailedRisk?.vendor_name || 'Vendor'} Risk Profile
              </h2>
            </div>

            {/* Risk Summary */}
            <Row gutter={24} style={{ marginBottom: '32px' }}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <div className="p5-label">Overall Risk Score</div>
                  <div className="p5-font-display" style={{
                    fontSize: '3rem',
                    color: (detailedRisk.profile?.overall_risk_score || 0) >= 50
                      ? 'var(--p5-rose-500)'
                      : 'var(--p5-emerald-500)'
                  }}>
                    {detailedRisk.profile?.overall_risk_score || 0}
                    <span style={{ fontSize: '1rem', color: 'var(--p5-slate-400)' }}>/100</span>
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <div className="p5-label">Risk Level</div>
                  <span className={`p5-tag ${getRiskTagClass(detailedRisk.profile?.risk_level)} p5-mt-sm`} style={{ fontSize: '1rem', padding: '8px 20px' }}>
                    {detailedRisk.profile?.risk_level?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <div className="p5-label">Risk Trend</div>
                  <div style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    marginTop: '8px',
                    color: detailedRisk.risk_trend === 'improving'
                      ? 'var(--p5-emerald-500)'
                      : detailedRisk.risk_trend === 'worsening'
                      ? 'var(--p5-rose-500)'
                      : 'var(--p5-slate-400)'
                  }}>
                    {detailedRisk.risk_trend === 'improving' && <RiseOutlined style={{ marginRight: '8px' }} />}
                    {detailedRisk.risk_trend === 'worsening' && <FallOutlined style={{ marginRight: '8px' }} />}
                    {detailedRisk.risk_trend?.charAt(0).toUpperCase() + detailedRisk.risk_trend?.slice(1) || 'Stable'}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Security & Compliance */}
            <div style={{ marginBottom: '32px' }}>
              <h4 className="p5-section-title" style={{ fontSize: '1.125rem' }}>
                <SecurityScanOutlined /> Security & Compliance
              </h4>
              <div className="p5-flex p5-gap-sm" style={{ flexWrap: 'wrap' }}>
                {[
                  { key: 'soc2_certified', label: 'SOC 2' },
                  { key: 'iso27001_certified', label: 'ISO 27001' },
                  { key: 'gdpr_compliant', label: 'GDPR' },
                  { key: 'hipaa_compliant', label: 'HIPAA' },
                  { key: 'pci_dss_compliant', label: 'PCI DSS' },
                ].map(({ key, label }) => (
                  <span
                    key={key}
                    className={`p5-tag ${detailedRisk.profile?.[key] ? 'p5-tag--success' : 'p5-tag--neutral'}`}
                    style={{ padding: '6px 14px' }}
                  >
                    {detailedRisk.profile?.[key] ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Third-Party Ratings */}
            <div style={{ marginBottom: '32px' }}>
              <h4 className="p5-section-title" style={{ fontSize: '1.125rem' }}>
                <TrophyOutlined /> Third-Party Ratings
              </h4>
              <Row gutter={16}>
                {[
                  { key: 'g2_rating', label: 'G2' },
                  { key: 'gartner_rating', label: 'Gartner' },
                  { key: 'capterra_rating', label: 'Capterra' },
                  { key: 'trustpilot_rating', label: 'Trustpilot' },
                ].map(({ key, label }) => (
                  <Col span={6} key={key}>
                    <div style={{
                      background: 'var(--p5-slate-100)',
                      padding: '16px',
                      borderRadius: 'var(--p5-radius-md)',
                      textAlign: 'center'
                    }}>
                      <div className="p5-label" style={{ marginBottom: '8px' }}>{label}</div>
                      {detailedRisk.profile?.[key] ? (
                        <Rate disabled allowHalf defaultValue={detailedRisk.profile[key]} style={{ fontSize: '16px' }} />
                      ) : (
                        <span className="p5-text-slate">-</span>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Risk Factors */}
            {detailedRisk.risk_factors?.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h4 className="p5-section-title" style={{ fontSize: '1.125rem' }}>
                  <AlertOutlined /> Risk Factors
                </h4>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {detailedRisk.risk_factors.map((factor, idx) => (
                    <div key={idx} style={{
                      background: 'var(--p5-slate-100)',
                      borderRadius: 'var(--p5-radius-md)',
                      padding: '16px',
                      borderLeft: `3px solid ${
                        factor.severity === 'critical' || factor.severity === 'high'
                          ? 'var(--p5-rose-500)'
                          : factor.severity === 'medium'
                          ? 'var(--p5-amber-500)'
                          : 'var(--p5-slate-300)'
                      }`
                    }}>
                      <div className="p5-flex p5-gap-sm" style={{ marginBottom: '8px' }}>
                        <span className={`p5-tag ${
                          factor.severity === 'critical' || factor.severity === 'high'
                            ? 'p5-tag--danger'
                            : factor.severity === 'medium'
                            ? 'p5-tag--warning'
                            : 'p5-tag--neutral'
                        }`}>
                          {factor.severity?.toUpperCase()}
                        </span>
                        <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)' }}>{factor.factor_name}</span>
                      </div>
                      <div style={{ color: 'var(--p5-navy-600)', marginBottom: factor.recommendation ? '8px' : 0 }}>
                        {factor.description}
                      </div>
                      {factor.recommendation && (
                        <div style={{ color: 'var(--p5-slate-400)', fontSize: '0.875rem' }}>
                          <InfoCircleOutlined style={{ marginRight: '6px' }} />
                          {factor.recommendation}
                        </div>
                      )}
                    </div>
                  ))}
                </Space>
              </div>
            )}

            {/* Recommendations */}
            {detailedRisk.recommendations?.length > 0 && (
              <div>
                <h4 className="p5-section-title" style={{ fontSize: '1.125rem' }}>
                  <CheckCircleOutlined /> Recommendations
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {detailedRisk.recommendations.map((rec, idx) => (
                    <li key={idx} style={{
                      color: 'var(--p5-navy-700)',
                      marginBottom: '8px',
                      lineHeight: 1.6
                    }}>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Data Source Modal */}
      <Modal
        title={null}
        open={sourceModalVisible}
        onCancel={() => {
          setSourceModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        className="p5-modal"
        okButtonProps={{ className: 'p5-btn p5-btn--accent' }}
        cancelButtonProps={{ className: 'p5-btn p5-btn--ghost' }}
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Add Data Source
          </h2>
        </div>
        <Form form={form} layout="vertical" onFinish={handleAddSource}>
          <Form.Item
            name="source_type"
            label={<span className="p5-label">Source Type</span>}
            rules={[{ required: true, message: 'Please select source type' }]}
          >
            <Select placeholder="Select source type" size="large">
              <Select.Option value="g2">G2 Crowd</Select.Option>
              <Select.Option value="gartner">Gartner</Select.Option>
              <Select.Option value="dnb">Dun & Bradstreet</Select.Option>
              <Select.Option value="newsapi">News API</Select.Option>
              <Select.Option value="custom">Custom</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="source_name" label={<span className="p5-label">Display Name</span>}>
            <Input placeholder="e.g., My Custom Source" size="large" />
          </Form.Item>
          <Form.Item name="api_key" label={<span className="p5-label">API Key</span>}>
            <Input.Password placeholder="Enter API key" size="large" />
          </Form.Item>
          <Form.Item name="base_url" label={<span className="p5-label">Base URL</span>}>
            <Input placeholder="https://api.example.com" size="large" />
          </Form.Item>
          <Form.Item
            name="sync_frequency_hours"
            label={<span className="p5-label">Sync Frequency (hours)</span>}
            initialValue={24}
          >
            <InputNumber min={1} max={168} style={{ width: '100%' }} size="large" />
          </Form.Item>
          <Form.Item name="is_enabled" label={<span className="p5-label">Enable Source</span>} valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MarketIntelligence;
